import React, { useState, useRef, useEffect } from 'react';
import { motion, useMotionValue, useTransform, useSpring, AnimatePresence } from 'framer-motion';

const LiquidGlassToggle = ({
  checked = false,
  onChange,
  variant = "primary",
  size = "medium",
  disabled = false,
  label = "",
  description = "",
  showIcon = true,
  animated = true,
  className = "",
  style = {},
  ...props
}) => {
  const [isChecked, setIsChecked] = useState(checked);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const toggleRef = useRef(null);
  
  // Motion values for advanced animations
  const scale = useMotionValue(1);
  const rotate = useMotionValue(0);
  const glowIntensity = useMotionValue(0);
  
  // Spring animations for smooth transitions
  const springConfig = { damping: 20, stiffness: 300 };
  const springScale = useSpring(scale, springConfig);
  const springRotate = useSpring(rotate, springConfig);
  const springGlow = useSpring(glowIntensity, springConfig);
  
  // Transform values for dynamic effects
  const glowOpacity = useTransform(springGlow, [0, 1], [0, 0.8]);
  const glowScale = useTransform(springGlow, [0, 1], [0.8, 1.2]);

  // Update internal state when prop changes
  useEffect(() => {
    setIsChecked(checked);
  }, [checked]);

  // Handle toggle state change
  const handleToggle = () => {
    if (disabled) return;
    
    const newState = !isChecked;
    setIsChecked(newState);
    
    // Trigger enhanced animations
    if (animated) {
      // Enhanced scale animation with bounce effect
      scale.set(1.15);
      rotate.set(newState ? 180 : 0);
      glowIntensity.set(1);
      
      // Smooth spring back to normal size
      setTimeout(() => {
        scale.set(1);
      }, 150);
      
      // Gradual glow fade
      setTimeout(() => {
        glowIntensity.set(0);
      }, 300);
    }
    
    onChange?.(newState);
  };

  // Variant styles
  const variants = {
    primary: {
      background: `
        linear-gradient(135deg, 
          rgba(0, 123, 255, 0.2) 0%, 
          rgba(0, 123, 255, 0.1) 50%, 
          rgba(0, 123, 255, 0.2) 100%
        ),
        radial-gradient(
          circle at 30% 30%, 
          rgba(0, 123, 255, 0.3) 0%, 
          transparent 50%
        )
      `,
      borderColor: 'rgba(0, 123, 255, 0.4)',
      shadowColor: 'rgba(0, 123, 255, 0.3)',
      textColor: 'rgba(255, 255, 255, 0.9)',
      activeColor: 'rgba(0, 123, 255, 0.8)',
      inactiveColor: 'rgba(255, 255, 255, 0.1)'
    },
    secondary: {
      background: `
        linear-gradient(135deg, 
          rgba(255, 255, 255, 0.1) 0%, 
          rgba(255, 255, 255, 0.05) 50%, 
          rgba(255, 255, 255, 0.1) 100%
        ),
        radial-gradient(
          circle at 70% 70%, 
          rgba(255, 255, 255, 0.15) 0%, 
          transparent 50%
        )
      `,
      borderColor: 'rgba(255, 255, 255, 0.3)',
      shadowColor: 'rgba(255, 255, 255, 0.2)',
      textColor: 'rgba(255, 255, 255, 0.9)',
      activeColor: 'rgba(255, 255, 255, 0.8)',
      inactiveColor: 'rgba(255, 255, 255, 0.1)'
    },
    success: {
      background: `
        linear-gradient(135deg, 
          rgba(40, 167, 69, 0.2) 0%, 
          rgba(40, 167, 69, 0.1) 50%, 
          rgba(40, 167, 69, 0.2) 100%
        ),
        radial-gradient(
          circle at 30% 30%, 
          rgba(40, 167, 69, 0.3) 0%, 
          transparent 50%
        )
      `,
      borderColor: 'rgba(40, 167, 69, 0.4)',
      shadowColor: 'rgba(40, 167, 69, 0.3)',
      textColor: 'rgba(255, 255, 255, 0.9)',
      activeColor: 'rgba(40, 167, 69, 0.8)',
      inactiveColor: 'rgba(255, 255, 255, 0.1)'
    },
    danger: {
      background: `
        linear-gradient(135deg, 
          rgba(220, 53, 69, 0.2) 0%, 
          rgba(220, 53, 69, 0.1) 50%, 
          rgba(220, 53, 69, 0.2) 100%
        ),
        radial-gradient(
          circle at 70% 70%, 
          rgba(220, 53, 69, 0.3) 0%, 
          transparent 50%
        )
      `,
      borderColor: 'rgba(220, 53, 69, 0.4)',
      shadowColor: 'rgba(220, 53, 69, 0.3)',
      textColor: 'rgba(255, 255, 255, 0.9)',
      activeColor: 'rgba(220, 53, 69, 0.8)',
      inactiveColor: 'rgba(255, 255, 255, 0.1)'
    },
    warning: {
      background: `
        linear-gradient(135deg, 
          rgba(255, 193, 7, 0.2) 0%, 
          rgba(255, 193, 7, 0.1) 50%, 
          rgba(255, 193, 7, 0.2) 100%
        ),
        radial-gradient(
          circle at 30% 30%, 
          rgba(255, 193, 7, 0.3) 0%, 
          transparent 50%
        )
      `,
      borderColor: 'rgba(255, 193, 7, 0.4)',
      shadowColor: 'rgba(255, 193, 7, 0.3)',
      textColor: 'rgba(255, 255, 255, 0.9)',
      activeColor: 'rgba(255, 193, 7, 0.8)',
      inactiveColor: 'rgba(255, 255, 255, 0.1)'
    },
    info: {
      background: `
        linear-gradient(135deg, 
          rgba(23, 162, 184, 0.2) 0%, 
          rgba(23, 162, 184, 0.1) 50%, 
          rgba(23, 162, 184, 0.2) 100%
        ),
        radial-gradient(
          circle at 70% 70%, 
          rgba(23, 162, 184, 0.3) 0%, 
          transparent 50%
        )
      `,
      borderColor: 'rgba(23, 162, 184, 0.4)',
      shadowColor: 'rgba(23, 162, 184, 0.3)',
      textColor: 'rgba(255, 255, 255, 0.9)',
      activeColor: 'rgba(23, 162, 184, 0.8)',
      inactiveColor: 'rgba(255, 255, 255, 0.1)'
    },
    purple: {
      background: `
        linear-gradient(135deg, 
          rgba(102, 16, 242, 0.2) 0%, 
          rgba(102, 16, 242, 0.1) 50%, 
          rgba(102, 16, 242, 0.2) 100%
        ),
        radial-gradient(
          circle at 30% 30%, 
          rgba(102, 16, 242, 0.3) 0%, 
          transparent 50%
        )
      `,
      borderColor: 'rgba(102, 16, 242, 0.4)',
      shadowColor: 'rgba(102, 16, 242, 0.3)',
      textColor: 'rgba(255, 255, 255, 0.9)',
      activeColor: 'rgba(102, 16, 242, 0.8)',
      inactiveColor: 'rgba(255, 255, 255, 0.1)'
    }
  };

  // Size styles
  const sizes = {
    small: {
      width: '40px',
      height: '24px',
      thumbSize: '18px',
      blur: '10px',
      shadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      border: '1px solid',
      fontSize: '0.75rem'
    },
    medium: {
      width: '52px',
      height: '32px',
      thumbSize: '24px',
      blur: '15px',
      shadow: '0 6px 20px rgba(0, 0, 0, 0.2)',
      border: '1px solid',
      fontSize: '0.875rem'
    },
    large: {
      width: '64px',
      height: '40px',
      thumbSize: '30px',
      blur: '20px',
      shadow: '0 8px 28px rgba(0, 0, 0, 0.25)',
      border: '1px solid',
      fontSize: '1rem'
    },
    xlarge: {
      width: '80px',
      height: '48px',
      thumbSize: '36px',
      blur: '25px',
      shadow: '0 12px 36px rgba(0, 0, 0, 0.3)',
      border: '1px solid',
      fontSize: '1.125rem'
    }
  };

  const currentVariant = variants[variant] || variants.primary;
  const currentSize = sizes[size] || sizes.medium;

  // Ensure numeric values for all sizes
  const widthNum = Number(currentSize.width.replace('px', ''));
  const heightNum = Number(currentSize.height.replace('px', ''));
  const thumbNum = Number(currentSize.thumbSize.replace('px', ''));
  const offset = (heightNum - thumbNum) / 2;
  const thumbTranslate = isChecked
    ? widthNum - thumbNum - offset
    : offset;

  return (
    <div
      className={`liquid-glass-toggle-container ${className}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...style
      }}
      onMouseEnter={() => !disabled && setIsHovered(true)}
      onMouseLeave={() => !disabled && setIsHovered(false)}
      onClick={handleToggle}
      {...props}
    >
      {/* Toggle Switch */}
      <motion.div
        ref={toggleRef}
        className="liquid-glass-toggle"
        style={{
          position: 'relative',
          width: currentSize.width,
          height: currentSize.height,
          borderRadius: '50px',
          background: currentVariant.background,
          backdropFilter: `blur(${currentSize.blur})`,
          WebkitBackdropFilter: `blur(${currentSize.blur})`,
          border: `${currentSize.border} ${currentVariant.borderColor}`,
          boxShadow: currentSize.shadow,
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          transformStyle: 'preserve-3d',
          perspective: '1000px',
          overflow: 'hidden'
        }}
        animate={{
          scale: isPressed ? 0.95 : 1,
          boxShadow: isHovered ? 
            `${currentSize.shadow}, 0 0 20px ${currentVariant.shadowColor}` : 
            currentSize.shadow
        }}
        whileHover={!disabled ? {
          scale: 1.05,
          filter: 'brightness(1.1)',
          transition: { duration: 0.2 }
        } : undefined}
        whileTap={!disabled ? {
          scale: 0.95,
          transition: { duration: 0.1 }
        } : undefined}
        onMouseDown={() => !disabled && setIsPressed(true)}
        onMouseUp={() => !disabled && setIsPressed(false)}
        onTouchStart={() => !disabled && setIsPressed(true)}
        onTouchEnd={() => !disabled && setIsPressed(false)}
      >
        {/* Glass overlay effect */}
        <motion.div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              linear-gradient(
                135deg,
                rgba(255, 255, 255, 0.15) 0%,
                transparent 50%,
                rgba(255, 255, 255, 0.05) 100%
              )
            `,
            borderRadius: 'inherit',
            pointerEvents: 'none',
            mixBlendMode: 'overlay'
          }}
          animate={{
            opacity: isHovered ? 0.8 : 0.4
          }}
          transition={{ duration: 0.3 }}
        />

        {/* Inner glow effect */}
        <motion.div
          style={{
            position: 'absolute',
            top: '2px',
            left: '2px',
            right: '2px',
            bottom: '2px',
            background: `
              radial-gradient(
                circle at 50% 0%,
                rgba(255, 255, 255, 0.1) 0%,
                transparent 70%
              )
            `,
            borderRadius: 'inherit',
            pointerEvents: 'none',
            filter: 'blur(1px)'
          }}
          animate={{
            opacity: isHovered ? 0.4 : 0.2
          }}
          transition={{ duration: 0.3 }}
        />

        {/* Track background */}
        <motion.div
          style={{
            position: 'absolute',
            top: offset,
            left: offset,
            right: offset,
            bottom: offset,
            borderRadius: 'inherit',
          }}
          animate={{
            background: isChecked ? currentVariant.activeColor : currentVariant.inactiveColor
          }}
          transition={{
            background: { duration: 0.35, ease: [0.4, 0, 0.2, 1] }
          }}
        />

        {/* Thumb */}
        <motion.div
          className="toggle-thumb"
          style={{
            position: 'absolute',
            top: offset,
            left: offset,
            width: thumbNum,
            height: thumbNum,
            borderRadius: '50%',
            background: `
              linear-gradient(135deg, 
                rgba(255, 255, 255, 0.9) 0%, 
                rgba(255, 255, 255, 0.7) 50%, 
                rgba(255, 255, 255, 0.9) 100%
              )
            `,
            boxShadow: `
              0 2px 8px rgba(0, 0, 0, 0.2),
              inset 0 1px 0 rgba(255, 255, 255, 0.8),
              inset 0 -1px 0 rgba(0, 0, 0, 0.1)
            `,
            zIndex: 2
          }}
          animate={{
            left: thumbTranslate,
            scale: isPressed ? 0.9 : 1,
            rotate: springRotate
          }}
          transition={{
            x: { duration: 0.35, ease: [0.4, 0, 0.2, 1] },
            scale: { duration: 0.15, ease: "easeOut" }
          }}
          whileHover={{
            scale: 1.05,
            transition: { duration: 0.2 }
          }}
        >
          {/* Thumb glow effect */}
          <motion.div
            style={{
              position: 'absolute',
              top: '-2px',
              left: '-2px',
              right: '-2px',
              bottom: '-2px',
              borderRadius: '50%',
              background: `radial-gradient(circle, ${currentVariant.shadowColor}, transparent)`,
              opacity: glowOpacity,
              scale: glowScale,
              pointerEvents: 'none'
            }}
          />

          {/* Thumb inner highlight */}
          <motion.div
            style={{
              position: 'absolute',
              top: '2px',
              left: '2px',
              right: '2px',
              bottom: '2px',
              borderRadius: '50%',
              background: `
                radial-gradient(
                  circle at 30% 30%,
                  rgba(255, 255, 255, 0.8) 0%,
                  transparent 70%
                )
              `,
              pointerEvents: 'none'
            }}
            animate={{
              opacity: isHovered ? 0.6 : 0.3
            }}
            transition={{ duration: 0.3 }}
          />

          {/* Icon */}
        </motion.div>

        {/* Ripple effect on toggle */}
        <AnimatePresence>
          {isPressed && (
            <motion.div
              initial={{ scale: 0, opacity: 0.9 }}
              animate={{ scale: 3, opacity: 0 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ 
                duration: 0.6,
                ease: "easeOut"
              }}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: `radial-gradient(circle, ${currentVariant.shadowColor}, transparent)`,
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                filter: 'blur(2px)'
              }}
            />
          )}
        </AnimatePresence>

        {/* Pulse effect on toggle */}
        <AnimatePresence>
          {isPressed && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0.6 }}
              animate={{ scale: 1.2, opacity: 0 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ 
                duration: 0.4,
                ease: "easeOut"
              }}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                background: `radial-gradient(circle, ${currentVariant.activeColor}, transparent)`,
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                filter: 'blur(1px)'
              }}
            />
          )}
        </AnimatePresence>

        {/* Hover glow effect */}
        {isHovered && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            style={{
              position: 'absolute',
              top: '-4px',
              left: '-4px',
              right: '-4px',
              bottom: '-4px',
              borderRadius: 'inherit',
              background: `linear-gradient(45deg, ${currentVariant.shadowColor}, transparent)`,
              zIndex: -1,
              filter: 'blur(8px)'
            }}
            transition={{ duration: 0.3 }}
          />
        )}
      </motion.div>

      {/* Label and Description */}
      {(label || description) && (
        <div style={{ flex: 1 }}>
          {label && (
            <div
              style={{
                color: currentVariant.textColor,
                fontSize: currentSize.fontSize,
                fontWeight: 500,
                marginBottom: description ? '4px' : 0,
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
              }}
            >
              {label}
            </div>
          )}
          {description && (
            <div
              style={{
                color: `${currentVariant.textColor}CC`,
                fontSize: `${parseInt(currentSize.fontSize) * 0.85}px`,
                opacity: 0.8,
                lineHeight: 1.4,
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
              }}
            >
              {description}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LiquidGlassToggle; 